import { Classes, Dialog } from "@blueprintjs/core";
import { FieldGroup } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import * as Yup from "yup";

import {
  FormateurSuiviApi,
  FormateurSuiviFichierUploadDto,
  FormateurSuiviFichierUploadDtoFromJSON
} from "../../../../../api";
import { FGWalterFileInput, FGWalterSelectInput, SmallFormGenerator } from "../../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

const StyledDialog = styled(Dialog)`
  width: 600px;
  height: auto;
  background-color: white;
`;

export interface IFormateurSuiviDocumentUploadDialogProps {
  idsuivi: number;
  dialogOpen: boolean;
  onClose: (refresh?: boolean) => void;
}

export const FormateurSuiviDocumentUploadDialog: React.FunctionComponent<IFormateurSuiviDocumentUploadDialogProps> = ({
  idsuivi,
  dialogOpen,
  onClose
}) => {
  const { t } = useTl();
  const api = useApiService(FormateurSuiviApi);

  const { data, loading, saveItem, saving, validationErrors } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => {
          return FormateurSuiviFichierUploadDtoFromJSON({ idformateurSuivi: idsuivi });
        },
        saveApiFn: async (d: FormateurSuiviFichierUploadDto) => {
          return api.formateurSuiviUploadFormateurSuiviFichier({
            FormateurSuiviFichierUploadDto: d
          });
        },
        onSaved: () => onClose(true)
      }),
      [api, idsuivi, onClose]
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape({
        idtypeSuiviFichier: Yup.number().nullable(),
        file: Yup.object()
          .nullable()
          .shape({
            fileName: Yup.string()
              .nullable()
              .required(t(ETLCodes.Required)),
            contentB64: Yup.string()
              .nullable()
              .required(t(ETLCodes.Required))
          })
      }),
    [t]
  );

  const [typesSuiviFichier, tLoading] = useReferential(a => a.referentialGetTypesSuiviFichier());

  return (
    <StyledDialog title={t(ETLCodes.UploadFichier)} isOpen={dialogOpen} onClose={() => onClose(false)}>
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          validationSchema={FormSchema}
          onCancel={() => onClose(false)}
          showDeleteButton={false}
          saving={saving}
          loading={loading}
          validationErrors={validationErrors}
        >
          <FieldGroup>
            <FGWalterFileInput name="file" label={t(ETLCodes.Fichier)} requiredMark />
            <FGWalterSelectInput
              name="idtypeSuiviFichier"
              label={t(ETLCodes.Type)}
              items={typesSuiviFichier}
              loading={tLoading}
            />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </StyledDialog>
  );
};
